"use client";
import { useState } from "react";
import { contactContent } from "@/lib/content";
import { fetchContactToken } from "@/lib/contact/token-client";
import { cn } from "@/lib/utils";

type State = { status: "idle" | "sending" | "sent" } | { status: "error"; message: string };

const FIELD =
  "w-full rounded-md border border-line bg-bg px-3.5 py-2.5 text-[14px] text-fg outline-none transition-colors placeholder:text-fg-faint focus:border-fg-muted";

export function ContactForm() {
  const t = contactContent.form;
  const [state, setState] = useState<State>({ status: "idle" });

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setState({ status: "sending" });

    // The token is fetched now rather than on page load, so it cannot expire while
    // someone is still writing.
    const token = await fetchContactToken();

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.get("name"),
          email: form.get("email"),
          message: form.get("message"),
          // Hidden field. A person leaves it empty; something automated does not.
          website: form.get("website"),
          token,
        }),
      });

      if (res.ok) {
        setState({ status: "sent" });
        return;
      }
      setState({
        status: "error",
        message: res.status === 429 ? t.errors.rate : res.status === 503 ? t.errors.config : t.errors.generic,
      });
    } catch {
      setState({ status: "error", message: t.errors.generic });
    }
  }

  if (state.status === "sent") {
    return (
      <div className="grid gap-4 rounded-md border border-line px-6 py-8">
        <p className="text-fg">{t.sent}</p>
        <button
          type="button"
          onClick={() => setState({ status: "idle" })}
          className="justify-self-start text-[13px] text-fg-muted underline underline-offset-4 transition-colors hover:text-fg"
        >
          {t.again}
        </button>
      </div>
    );
  }

  const sending = state.status === "sending";

  return (
    <form onSubmit={onSubmit} className="grid gap-4">
      <Field id="name" label={t.name.label}>
        <input id="name" name="name" required maxLength={100} placeholder={t.name.placeholder} className={FIELD} />
      </Field>
      <Field id="email" label={t.email.label}>
        <input
          id="email"
          name="email"
          type="email"
          required
          maxLength={254}
          placeholder={t.email.placeholder}
          className={FIELD}
        />
      </Field>
      <Field id="message" label={t.message.label}>
        <textarea
          id="message"
          name="message"
          required
          maxLength={5000}
          rows={6}
          placeholder={t.message.placeholder}
          className={cn(FIELD, "resize-y")}
        />
      </Field>

      {/* Not display:none - some bots skip hidden inputs, but not ones pushed off-screen. */}
      <div aria-hidden className="absolute -left-[9999px]">
        <label htmlFor="website">Website</label>
        <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={sending}
          className="cursor-pointer rounded-full bg-btn px-5 py-2.5 text-[14px] text-btn-fg transition-opacity hover:opacity-90 disabled:cursor-wait disabled:opacity-60"
        >
          {sending ? `${t.sending}...` : t.submit}
        </button>
        {state.status === "error" && (
          <p role="alert" className="text-[13px] text-accent">
            {state.message}
          </p>
        )}
      </div>
    </form>
  );
}

function Field({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div className="grid gap-1.5">
      <label htmlFor={id} className="text-[13px] text-fg-muted">
        {label}
      </label>
      {children}
    </div>
  );
}
